import React, { useEffect, useRef } from "react";
import { QueryResult, useQueryCache } from "react-query";
import { ChargesTable, IFeeListItem } from "./ChargesTable";
import "./ChargesCard.scss";

interface IChargesTableWithPending {
  feeList: IFeeListItem[];
  activeFees: number;
  balance: number | undefined;
  subtotal?: number;
  mainAmount?: number;
  paid: number;
  user?: "citizen";
  type?: "payment" | "permits-waive-fee";
  numTransactions: React.MutableRefObject<number>;
  pending: number[];
  setPending: React.Dispatch<React.SetStateAction<number[]>>;
  setProcessing?: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess?: (charges?: unknown) => void;
  refetch: () => void;
  permitQueryRef: React.MutableRefObject<QueryResult<any | null, unknown>>;
  queryName: string;
}
export const ChargesTableWithPending = ({
  feeList,
  activeFees,
  balance,
  mainAmount,
  paid,
  user,
  type = "payment",
  numTransactions,
  pending,
  setPending,
  setProcessing,
  onSuccess,
  refetch,
  permitQueryRef,
  queryName,
  subtotal,
}: IChargesTableWithPending) => {
  const isPolling = useRef<boolean>(false);
  const timeoutRef = useRef<number>(0);
  const queryCache = useQueryCache();

  // Called when component is unmounted- cancels setTimeout so it won't refetch infinitely
  function useComponentWillUnmount(
    cleanupCallback = () => {
      queryCache.invalidateQueries("permits");
      queryCache.invalidateQueries(queryName);
      numTransactions.current = 0;
      setPending([]);
      isPolling.current = false;
      clearTimeout(timeoutRef.current);
    },
  ) {
    const callbackRef = React.useRef(cleanupCallback);
    callbackRef.current = cleanupCallback; // always up to date
    React.useEffect(() => {
      return () => callbackRef.current();
    }, []);
  }
  useComponentWillUnmount();

  // Called any time permitQuery is fetched
  useEffect(() => {
    // If there are currently no pending permits, check if there are any pending transactions and add them to the pending list
    if (
      pending.length === 0 &&
      permitQueryRef.current.isSuccess &&
      permitQueryRef.current.data !== null &&
      !(permitQueryRef?.current?.data as any)?.error
    ) {
      const permitData = permitQueryRef?.current?.data?.data.attributes;
      const transactions = permitData?.transactions ?? [];
      numTransactions.current =
        transactions?.filter((transactions: any) => transactions.attributes.category === null).length ?? 0; // Count # of transactions (not including disputes)

      // Find pending transactions from backend data
      const pendingTransactions = [];
      for (const transaction of transactions)
        if (transaction.attributes.status === "pending" && transaction.attributes.category === null)
          pendingTransactions.push(transaction.attributes.main_amount);

      // If pending transactions are found, set state
      if (pendingTransactions.length !== 0) setPending(pendingTransactions);
      // If no pending transactions are found, ensure that TOTAL_FEE - PAID = BALANCE_DUE, as sometimes balance is out of sync. If balance is not synced, add outstanding balance to the pending transactions list
      else {
        const pendingAmt = permitData
          ? permitData["total-fee-in-cents"] - permitData["total-paid-in-cents"] - permitData["balance-due"]
          : 0;
        if (pendingAmt !== 0) setPending([-pendingAmt]);
      }
    }
  }, [permitQueryRef.current]);

  // Called any time a new pending transaction is added
  useEffect(() => {
    if (pending.length === 0 || isPolling.current) return;

    isPolling.current = true;
    // Poll permit to check if it has updated data. If not, wait 1 second, then poll again. Repeat until the data is updated
    const pollPermit = () => {
      refetch();
      const permitData = permitQueryRef.current?.data?.data?.attributes;
      if (permitData) {
        const transactions = permitData.transactions;
        const isBalanceSynced =
          permitData["total-fee-in-cents"] - permitData["total-paid-in-cents"] === permitData["balance-due"];
        const pendingTransactions = transactions.filter(
          (transaction: any) => transaction.attributes.status === "pending" && transaction.attributes.category === null,
        );
        const permitNumTransactions = transactions.filter(
          (transactions: any) => transactions.attributes.category === null,
        );

        // Stop polling if: TOTAL_FEE - TOTAL_PAID = BALANCE AND no pending transactions AND transaction count matches up (not counting fraudulent)
        if (
          isBalanceSynced &&
          pendingTransactions.length === 0 &&
          numTransactions.current === permitNumTransactions.length
        ) {
          queryCache.invalidateQueries("permits");
          isPolling.current = false;
          setProcessing?.(false);
          setPending([]);
          onSuccess?.();
          return;
        }
      }

      const timeoutId = setTimeout(() => {
        pollPermit();
      }, 1000);
      timeoutRef.current = timeoutId;
    };
    pollPermit();
  }, [pending]);

  const pendingAmount = pending.reduce((accumulator: number, transaction: number) => {
    return accumulator + Number(transaction);
  }, 0); // Sum pending array to get pending amount

  return (
    <>
      <ChargesTable
        feeList={feeList}
        balance={balance}
        mainAmount={mainAmount}
        paid={paid}
        pending={pendingAmount}
        activeFees={activeFees}
        user={user}
        type={type}
        subtotal={subtotal}
      />
    </>
  );
};
